/* ExportAction.js */

"use strict";
const _ = require("lodash");
const FileName = require("@gluenetworks/steve_utilities").fileName;

class ExportAction {
    constructor(
        logger,
        gameEditor,
        activeView,
        NetworkDiscoveryPlan,
        userSession,
        steveIo,
        Credentials
    ) {
        this._logger = logger;
        this._activeView = activeView;
        this._NetworkDiscoveryPlanDef = NetworkDiscoveryPlan;
        this._activeOrgId = userSession.activeOrganization.id;
        this._fileFactory = steveIo.fileFactory;
        this._gameEditor = gameEditor;
        this._credentialsEntityDef = Credentials;
        this._getReplacementValue = this._getReplacementValue.bind(this);
        this.run = this.run.bind(this);
        this._fileNamer = FileName;
    }

    run() {
        let result = [];
        let entityIds = this._activeView.getSelectedPlanIds();
        let planName = "";

        if (!_.isEmpty(entityIds)) {
            let entitiesProm = [];
            let entities;
            if (entityIds && entityIds.selectedRows) {
                entitiesProm = entityIds.selectedRows.map((id) => {
                    return this._NetworkDiscoveryPlanDef.loadNetworkDiscoveryPlan(
                        this._activeOrgId,
                        id
                    );
                });
            }

            const engineZones = _.get(this._gameEditor, "_activeView._engineZoneValues", {});

            Promise.all(entitiesProm).then(async (results) => {
                entities = results;

                if (!_.isEmpty(entities)) {
                    entities.forEach((entity) => {
                        const properties = _.clone(entity.properties);
                        const engineZoneObj = Object.values(engineZones).find(
                            (i) => i.name === entity.getEngineZoneId()
                        );
                        const engineZone = engineZoneObj.title;
                        properties.engineZone = engineZone;
                        properties.scheduleEnabled = false;
                        result.push(
                            _.omit(properties, [
                                "lastRunOn",
                                "lastRunBy",
                                "lastWorkId",
                                "status",
                                "engineZoneId",
                                "scheduleWorkInput",
                                "#rel",
                                "ipRanges",
                            ])
                        );
                    });
                }

                result = await this._replaceKey(result, "credentials", this._getReplacementValue);
                result = await this._replaceKey(
                    result,
                    "enablePasswords",
                    this._getReplacementValue
                );

                if (entities.length === 1) {
                    planName = _.get(entities, ["0", "name"], "").trim();
                    planName = planName ? planName + "-" : "";
                }

                this._fileFactory.saveJsonAsFile(
                    JSON.stringify(result),
                    this._fileNamer({
                        solution: "DE",
                        general: "NetworkDiscoveryPlanExport",
                        specific: planName,
                        suffix: "json",
                    })
                );

                return Promise.resolve();
            });
        }
    }

    /**
     * Function to replace all credentials values replacing the guid with the cred name.
     * Will find all occurances of keyToFind and replace with value returned by async newValueCallback
     * This function can be repurposed for other uses.
     * @param {*} jsonObj
     * @param {*} keyToFind
     * @param {*} newValueCallback
     * @returns Object with Keys returned. "credentials" in this case.
     */
    async _replaceKey(jsonObj, keyToFind, newValueCallback) {
        if (typeof jsonObj !== "object" || jsonObj === null) {
            return jsonObj;
        }

        if (Array.isArray(jsonObj)) {
            for (let i = 0; i < jsonObj.length; i++) {
                jsonObj[i] = await this._replaceKey(jsonObj[i], keyToFind, newValueCallback);
            }
        } else {
            for (const key in jsonObj) {
                if (jsonObj.hasOwnProperty(key)) {
                    // If the current key matches keyToFind, replace its value with newValue
                    if (key === keyToFind && _.isEmpty(_.get(jsonObj, `${keyToFind}.@ref`))) {
                        jsonObj[key] = await newValueCallback(jsonObj[key]);
                    } else {
                        if (
                            !_.isEmpty(_.get(jsonObj, `${keyToFind}.@ref`)) ||
                            (keyToFind === "enablePasswords" &&
                                !_.isEmpty(_.get(jsonObj, `enablePassword.@ref`)))
                        ) {
                            if (keyToFind === "enablePasswords") {
                                jsonObj = this._replaceObjWithCred(jsonObj, "enablePassword");
                            } else {
                                jsonObj = this._replaceObjWithCred(jsonObj, keyToFind);
                            }
                        }
                        // Recursively call replaceKey for nested objects
                        jsonObj[key] = await this._replaceKey(
                            jsonObj[key],
                            keyToFind,
                            newValueCallback
                        );
                    }
                }
            }
        }
        return jsonObj;
    }

    _replaceObjWithCred(jsonObj, keyToFind) {
        const credential = _.get(jsonObj, `${keyToFind}.@ref.instance.instanceId`, false);

        if (credential) {
            delete jsonObj[keyToFind]["@ref"].instance.organizationId;
        }

        return jsonObj;
    }

    async _getReplacementValue(oldValue) {
        if (_.isString(oldValue)) {
            return await this._credentialsEntityDef.convertTemplateStringCredIdsToNames(
                this._activeOrgId,
                oldValue
            );
        } else if (_.isArray(oldValue)) {
            await oldValue.forEach(async (val, idx) => {
                oldValue[idx] =
                    await this._credentialsEntityDef.convertTemplateStringCredIdsToNames(
                        this._activeOrgId,
                        val
                    );
            });
            return oldValue;
        }
    }
}
module.exports = ExportAction;

ExportAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.entities.NetworkDiscoveryPlan",
    "$teve.userSession",
    "$teve.io",
    "$teve.entities.Credentials",
];

ExportAction.$displayDetails = {
    type: "Action",
    name: "ExportAction",
    icon: "configdrift.export_devices",
    shown: true,
    title: "Export Discovery Plan",
    order: 6,
};
